import type { Deal } from '../api';

/**
 * A deal rendered as a physical price tag: punch hole, hanging string, price
 * set larger than anything else on the card. The order is deliberate —
 * price, then what it is, then where and how far — because that's the order a
 * deal hunter actually decides in.
 */
export function DealTag({ deal, onOpen }: { deal: Deal; onOpen: (id: string) => void }) {
  const dead = deal.status !== 'active';
  // A range beats the low end alone, and "Sale" beats a bare dash.
  const price = deal.price.rangeDisplay ?? deal.price.display ?? 'Sale';
  const stock = deal.availability.stockStatus;

  // Prefer the per-item price where the pack has a count — "$0.92 each" over
  // "$3.11/L", because a can is the thing you pick up. Fall back to the measure
  // for anything sold by size alone, like a 2 L bottle.
  const unitNote =
    deal.unit?.count && deal.unit.perItemDisplay
      ? `${deal.unit.count} × ${deal.unit.perItemDisplay}`
      : (deal.unit?.perMeasureDisplay ?? null);

  return (
    <button
      className={`tag${dead ? ' tag-dead' : ''}`}
      onClick={() => onOpen(deal.id)}
      aria-label={`${price}, ${deal.itemName}`}
    >
      <div className="tag-top">
        <span className="tag-cat">
          {deal.categoryEmoji} {deal.categoryLabel}
        </span>
        <span
          className={`tag-score${deal.votes.score > 0 ? ' pos' : deal.votes.score < 0 ? ' neg' : ''}`}
        >
          {deal.votes.score > 0 ? '+' : ''}
          {deal.votes.score}
        </span>
      </div>

      <span className="tag-price">
        {price}
        {deal.price.wasDisplay && <span className="tag-was">{deal.price.wasDisplay}</span>}
        {deal.price.discountPct != null && <span className="tag-off">−{deal.price.discountPct}%</span>}
      </span>

      <p className="tag-name">{deal.itemName}</p>

      <div className="tag-meta">
        {/*
          What a case actually costs per can. Sits ahead of the merchant because
          for anything sold in a pack it is the number that decides the deal —
          $22 for Red Bull means nothing until you know it is 24 of them — and
          because it is what an alert's ceiling is compared against, so a card
          that omitted it could not explain why it was pushed.
        */}
        {unitNote && <span className="tag-unit">{unitNote}</span>}
        {deal.merchant && <span>{deal.merchant}</span>}
        {deal.distanceDisplay ? (
          <span className="near">{deal.distanceDisplay} away</span>
        ) : deal.availability.online ? (
          <span>Online</span>
        ) : null}
        {stock === 'low' && <span className="urgent">Low stock</span>}
        {stock === 'sold_out' && <span className="urgent">Sold out</span>}
        {deal.couponCode && <span>Code {deal.couponCode}</span>}
        <span style={{ marginLeft: 'auto' }}>{relativeTime(deal.postedAt)}</span>
      </div>
    </button>
  );
}

export function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}
